import { styled } from '@mui/material';
import { DefaultProps, ValidatedState } from '../../../../common/types';
import { CtrlDateField } from '../../../../components/controls/CtrlDateField';
import { ChangeableModel } from '../../../../model/model';
import { CtrlButton } from '../../../../components/controls/CtrlButton';
import { CloseIcon, PlusIcon } from '../../../../theme/icons';
import { CtrlIconButton } from '../../../../components/controls/CtrlIconButton';
import { ReferralModel } from '../../../ReferralModel';
import { validateDateFieldFormat } from '../../../../common/validationHelpers';

type RxInfo = {
    rxDate: Date | undefined;
    rxDateDateString?: string | undefined;
};

export const defaultRxInformationState = {
    rxInformation: [
        {
            rxDate: undefined as Date | undefined,
            rxDateDateString: undefined as string | undefined,
        },
    ] as RxInfo[],
};

export interface RxInformationState extends ValidatedState {
    rxInformation: RxInfo[];
}

export class RxInformationModel<T extends RxInformationState> extends ChangeableModel<T> {
    addRxDate = () => {
        this.state.update((state) => {
            state.rxInformation.push({
                rxDate: undefined,
                rxDateDateString: undefined,
            });
        });
    };

    removeRxDate = (index: number) => {
        this.state.get().rxInformation.forEach((_, idx) => {
            this.clearErrorMessages(`rxInformation[${idx}].rxDate`);
            this.validators = this.validators.filter((v) => !v.fieldName.startsWith(`rxInformation[${idx}]`));
        });
        this.state.update((state) => {
            state.rxInformation.splice(index, 1);
        });
    };

    handleRxDateChange = (rxDate: Date | undefined, index: number) => {
        this.clearErrorMessages(`rxInformation[${index}].rxDate`);
        this.state.update((state) => {
            state.rxInformation[index].rxDate = rxDate;
        });
    };

    handleStringRxDateChange = (rxDateStr: string, index: number) => {
        this.state.update((state) => {
            state.rxInformation[index].rxDateDateString = rxDateStr;
        });
    };

    validate(referralModel: ReferralModel): void {
        const rxInfo = this.state.get().rxInformation;
        rxInfo.forEach((_, idx) => {
            if (!this.validators.find((v) => v.fieldName === `rxInformation[${idx}].rxDate`)) {
                this.validators.push({
                    fieldName: `rxInformation[${idx}].rxDate`,
                    fieldLabel: `Rx Date ${idx + 1}`,
                    validateFunctions: [
                        validateDateFieldFormat(() => this.state.get().rxInformation[idx].rxDateDateString),
                    ],
                });
            }
        });
        super.validate(referralModel);
    }
}

export interface RxInformationProps extends DefaultProps {
    rxInfo: RxInfo[];
    allowMultipleDates?: boolean;
    handleRxDateChange: (rxDate: Date | undefined, index: number) => void;
    handleStringRxDateChange: (rxDateStr: string, index: number) => void;
    removeRxDate: (index: number) => void;
    addRxDate: () => void;
    getErrorMessage: (fieldName: string) => string | undefined;
}

const MAXIMUM_RX_DATES = 2;

export function RxInformationComponent(props: RxInformationProps) {
    const {
        rxInfo,
        allowMultipleDates,
        handleRxDateChange,
        handleStringRxDateChange,
        removeRxDate,
        addRxDate,
        getErrorMessage,
    } = props;

    return (
        <div className={props.className}>
            {rxInfo.map((info, index) => {
                const isMultiple = rxInfo.length > 1;
                const rxLabel = isMultiple ? `RX Date ${index + 1}` : 'RX Date';

                return (
                    <div className="fields-container">
                        <div className="span-6 rx-date-field">
                            <CtrlDateField
                                label={rxLabel}
                                value={info.rxDate}
                                onChange={(rxDate) => handleRxDateChange(rxDate, index)}
                                onTextChange={(rxDateStr) => handleStringRxDateChange(rxDateStr, index)}
                                error={getErrorMessage(`rxInformation[${index}].rxDate`)}
                            />
                            <div className="rx-date-actions">
                                {isMultiple && (
                                    <CtrlIconButton onClick={() => removeRxDate(index)} size="small">
                                        <CloseIcon />
                                    </CtrlIconButton>
                                )}
                            </div>
                         </div>
                    </div>
                );
            })}
            {allowMultipleDates && (
                <div className="add-rx-date fields-container">
                    <div className="span-6">
                        <CtrlButton endIcon={<PlusIcon />} onClick={addRxDate} disabled={rxInfo.length >= MAXIMUM_RX_DATES}>
                            Add RX Date
                        </CtrlButton>
                    </div>
                </div>
            )}
        </div>
    );
}

export const RxInformation = styled(RxInformationComponent, { name: 'RxInformation' })(() => ({
    marginTop: 20,
    '.rx-date-field': {
        position: 'relative',
    },
    '.rx-date-actions': {
        position: 'absolute',
        right: '-26px',
        top: '4px',
    },
    '.add-rx-date': {
        marginTop: '0px !important',
    },
}));
